/**
 * OwnershipEditor — structured ownership stakes editor.
 *
 * Responsibility: CRUD for OwnershipStake rows (ownerName, percentage, note).
 * Renders the live ownership list, add-row form, and per-row inline editing.
 *
 * Query key: ["entity-ownership", entityId]
 * Invalidates on add/delete: same + ["entity", entityId] + ["changelog-recent"]
 * Invalidates on edit: ["entity-ownership", entityId] + ["entity", entityId]
 *                      + ["entities"] + ["renewals"]
 */

import { useState, useId, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { cn } from "../lib/cn";
import {
  fetchOwnership,
  createOwnershipStake,
  deleteOwnershipStake,
  patchOwnershipStake,
} from "../lib/dataFetchers";
import { extractApiError } from "../lib/api";
import { Button } from "./ui/Button";
import { FieldActions } from "./FieldActions";
import { FieldEditHistory } from "./FieldEditHistory";
import { FieldHistoryPopover } from "./FieldHistoryPopover";
import { useFieldHasHistory } from "../lib/EntityHistoryFieldsContext";
import type { OwnershipStake } from "../lib/types";

// ─── Query invalidation keys ──────────────────────────────────────────────────

function invalidationKeys(entityId: string) {
  return [
    { queryKey: ["entity-ownership", entityId] },
    { queryKey: ["entity", entityId] },
    { queryKey: ["changelog-recent"] },
    { queryKey: ["renewals"] },
    { queryKey: ["entities"] },
  ];
}

function editInvalidationKeys(entityId: string) {
  return [
    { queryKey: ["entity-ownership", entityId] },
    { queryKey: ["entity", entityId] },
    { queryKey: ["entities"] },
    { queryKey: ["renewals"] },
  ];
}

// ─── Add stake form (inline) ──────────────────────────────────────────────────

interface AddStakeFormProps {
  entityId: string;
  onDone: () => void;
}

function AddStakeForm({ entityId, onDone }: AddStakeFormProps): JSX.Element {
  const { t } = useTranslation("typed_fields");
  const qc = useQueryClient();
  const uid = useId();
  const [ownerName, setOwnerName] = useState("");
  const [percentage, setPercentage] = useState<string>("");
  const [note, setNote] = useState("");

  const ids = {
    ownerName: `${uid}-owner-name`,
    percentage: `${uid}-percentage`,
    note: `${uid}-note`,
  };

  const mutation = useMutation({
    mutationFn: () =>
      createOwnershipStake(entityId, {
        ownerName: ownerName.trim(),
        percentage: percentage !== "" ? parseFloat(percentage) : null,
        note: note.trim() || null,
        comment: null,
      }),
    onSuccess: () => {
      toast.success(t("save_success"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onDone();
    },
    onError: (err) => toast.error(extractApiError(err) || t("save_error")),
  });

  const canSubmit = ownerName.trim().length > 0 && !mutation.isPending;

  return (
    <div className="border border-accent/20 rounded-[10px] p-4 bg-accent-tint space-y-3 mt-2">
      <p className="text-[10px] font-bold tracking-[.05em] uppercase text-dim">{t("add_stake")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor={ids.ownerName} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("owner_name_label")} *
          </label>
          <input
            id={ids.ownerName}
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder={t("owner_name_placeholder")}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
        <div>
          <label htmlFor={ids.percentage} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("percentage_label")}
          </label>
          <input
            id={ids.percentage}
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            placeholder={t("percentage_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
        <div>
          <label htmlFor={ids.note} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("note_label")}
          </label>
          <input
            id={ids.note}
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("note_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="primary" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {t("save_button")}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          {t("cancel_button")}
        </Button>
      </div>
    </div>
  );
}

// ─── Stake row edit form ──────────────────────────────────────────────────────

interface StakeEditFormProps {
  entityId: string;
  stake: OwnershipStake;
  onDone: () => void;
  pencilRef: React.RefObject<HTMLButtonElement | null>;
}

function StakeEditForm({ entityId, stake, onDone, pencilRef }: StakeEditFormProps): JSX.Element {
  const { t } = useTranslation(["ownership", "typed_fields"]);
  const qc = useQueryClient();
  const uid = useId();

  const [ownerName, setOwnerName] = useState(stake.ownerName);
  const [percentage, setPercentage] = useState(
    stake.percentage !== null ? String(stake.percentage) : "",
  );
  const [note, setNote] = useState(stake.note ?? "");

  const [ownerNameError, setOwnerNameError] = useState("");
  const [percentageError, setPercentageError] = useState("");

  const ownerNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ownerNameRef.current?.focus();
  }, []);

  const ids = {
    ownerName: `${uid}-edit-owner-name`,
    percentage: `${uid}-edit-percentage`,
    note: `${uid}-edit-note`,
    ownerNameError: `${uid}-edit-owner-name-err`,
    percentageError: `${uid}-edit-percentage-err`,
  };

  const mutation = useMutation({
    mutationFn: () =>
      patchOwnershipStake(entityId, stake.id, {
        ownerName: ownerName.trim(),
        percentage: percentage !== "" ? parseFloat(percentage) : null,
        note: note.trim() || null,
        comment: null,
        expectedUpdatedAt: stake.updatedAt,
      }),
    onSuccess: () => {
      toast.success(t("ownership:edit_success_toast"));
      editInvalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onDone();
      pencilRef.current?.focus();
    },
    onError: (err) => {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        toast.error(t("ownership:edit_conflict_toast"));
        void qc.invalidateQueries({ queryKey: ["entity-ownership", entityId] });
        onDone();
        pencilRef.current?.focus();
      } else {
        toast.error(extractApiError(err) || t("ownership:edit_error_toast"));
      }
    },
  });

  function validate(): boolean {
    let valid = true;
    setOwnerNameError("");
    setPercentageError("");
    if (!ownerName.trim()) {
      setOwnerNameError(t("ownership:owner_name_required"));
      valid = false;
    }
    if (percentage !== "") {
      const val = parseFloat(percentage);
      if (isNaN(val) || val < 0 || val > 100) {
        setPercentageError(t("ownership:percentage_invalid"));
        valid = false;
      }
    }
    return valid;
  }

  function handleSave() {
    if (validate()) mutation.mutate();
  }

  function handleCancel() {
    onDone();
    pencilRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); handleCancel(); }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSave(); }
  }

  return (
    <fieldset
      aria-label={t("ownership:edit_stake_title")}
      className="border border-accent/20 rounded-[10px] p-4 bg-accent-tint space-y-3 mt-2"
    >
      <legend className="text-[10px] font-bold tracking-[.05em] uppercase text-dim px-0.5">
        {t("ownership:edit_stake_title")}
      </legend>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor={ids.ownerName} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("typed_fields:owner_name_label")} *
          </label>
          <input
            ref={ownerNameRef}
            id={ids.ownerName}
            type="text"
            value={ownerName}
            onChange={(e) => { setOwnerName(e.target.value); setOwnerNameError(""); }}
            onKeyDown={handleKeyDown}
            aria-describedby={ownerNameError ? ids.ownerNameError : undefined}
            aria-invalid={ownerNameError ? true : undefined}
            className={cn(
              "h-9 w-full px-3 rounded-lg border bg-surface text-base",
              "focus:ring-2 focus:ring-accent/14 focus:outline-none",
              ownerNameError
                ? "border-red focus:border-red"
                : "border-border focus:border-accent",
            )}
          />
          {ownerNameError && (
            <span id={ids.ownerNameError} role="alert" className="text-[11.5px] text-red-text mt-1 block">
              {ownerNameError}
            </span>
          )}
        </div>

        <div>
          <label htmlFor={ids.percentage} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("typed_fields:percentage_label")}
          </label>
          <input
            id={ids.percentage}
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={percentage}
            onChange={(e) => { setPercentage(e.target.value); setPercentageError(""); }}
            onKeyDown={handleKeyDown}
            placeholder={t("typed_fields:percentage_placeholder")}
            aria-describedby={percentageError ? ids.percentageError : undefined}
            aria-invalid={percentageError ? true : undefined}
            className={cn(
              "h-9 w-full px-3 rounded-lg border bg-surface text-base",
              "focus:ring-2 focus:ring-accent/14 focus:outline-none",
              percentageError
                ? "border-red focus:border-red"
                : "border-border focus:border-accent",
            )}
          />
          {percentageError && (
            <span id={ids.percentageError} role="alert" className="text-[11.5px] text-red-text mt-1 block">
              {percentageError}
            </span>
          )}
        </div>

        <div>
          <label htmlFor={ids.note} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("typed_fields:note_label")}
          </label>
          <input
            id={ids.note}
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("typed_fields:note_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
      </div>

      <FieldEditHistory entityId={entityId} field="ownership_stake.ownerName" />
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Button variant="primary" disabled={mutation.isPending} onClick={handleSave}>
          {mutation.isPending ? "…" : t("ownership:edit_save")}
        </Button>
        <Button variant="secondary" onClick={handleCancel}>
          {t("ownership:edit_cancel")}
        </Button>
      </div>
    </fieldset>
  );
}

// ─── Stake row ────────────────────────────────────────────────────────────────

interface StakeRowProps {
  entityId: string;
  stake: OwnershipStake;
  canEdit: boolean;
}

function StakeRow({ entityId, stake, canEdit }: StakeRowProps): JSX.Element {
  const { t } = useTranslation(["typed_fields", "ownership"]);
  const { t: tAudit } = useTranslation("audit");
  const qc = useQueryClient();
  const hasHistory =
    useFieldHasHistory("ownership_stake.ownerName") ||
    useFieldHasHistory("ownership_stake.percentage") ||
    useFieldHasHistory("ownership_stake_created") ||
    useFieldHasHistory("ownership_stake_deleted");
  const [deleteComment, setDeleteComment] = useState("");
  const [showDeleteInput, setShowDeleteInput] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const pencilRef = useRef<HTMLButtonElement>(null);

  const deleteMutation = useMutation({
    mutationFn: () => deleteOwnershipStake(entityId, stake.id, deleteComment),
    onSuccess: () => {
      toast.success(t("typed_fields:save_success"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      setShowDeleteInput(false);
      setDeleteComment("");
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  const locale = i18n.language;
  const formatDisplayDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });

  return (
    <li className="py-[6px] border-b border-border-2 last:border-b-0 group">
      <div className="flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-3">
        <div className="flex-1 min-w-0 space-y-0.5">
          <span data-field="stake.ownerName" className="text-[13.5px] font-semibold text-text block truncate">
            {stake.ownerName}
          </span>
          <div className="flex items-center gap-2">
            {stake.percentage !== null ? (
              <span data-field="stake.percentage" className="text-[12px] text-dim tabular-nums">
                {stake.percentage.toFixed(2)}%
              </span>
            ) : (
              <span data-field="stake.percentage" className="text-[12px] text-dim">—%</span>
            )}
            {stake.note ? (
              <span data-field="stake.note" className="text-[12px] text-dim truncate max-w-[160px]">
                ({stake.note})
              </span>
            ) : (
              <span data-field="stake.note" className="sr-only">—</span>
            )}
          </div>
          <p
            data-testid="stake-timestamps"
            data-field="stake.createdAt"
            className="text-[11px] text-dim"
          >
            {formatDisplayDate(stake.createdAt)} · {formatDisplayDate(stake.updatedAt)}
          </p>
        </div>

        {canEdit && !showEditForm && (
          <div className="shrink-0 flex items-center gap-1.5">
            <FieldActions
              fieldLabel={t("ownership:edit_stake_label", { name: stake.ownerName })}
              canEdit={true}
              showClock={hasHistory}
              clockTrigger={hasHistory ? (
                <FieldHistoryPopover
                  entityId={entityId}
                  field="ownership_stake.ownerName"
                  triggerAriaLabel={tAudit("open_field_history", { field: stake.ownerName })}
                />
              ) : undefined}
              onEdit={() => { setShowEditForm(true); setShowDeleteInput(false); setDeleteComment(""); }}
              editButtonRef={pencilRef}
            />

            {showDeleteInput ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={deleteComment}
                  onChange={(e) => setDeleteComment(e.target.value)}
                  placeholder={t("typed_fields:comment_placeholder")}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className={cn(
                    "h-8 px-2.5 rounded-lg border border-border bg-surface-2 text-[12.5px]",
                    "focus:border-red focus:ring-2 focus:ring-red/14 focus:outline-none",
                    "w-44",
                  )}
                />
                <button
                  type="button"
                  disabled={!deleteComment.trim() || deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                  className="h-8 px-2.5 rounded-[7px] bg-red text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 min-w-[60px]"
                >
                  {deleteMutation.isPending ? "…" : t("typed_fields:delete_stake")}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDeleteInput(false); setDeleteComment(""); }}
                  className="h-8 px-2 rounded-[7px] border border-border text-[12px] text-text hover:bg-surface-2"
                >
                  {t("typed_fields:cancel_button")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteInput(true)}
                title={t("typed_fields:delete_stake")}
                aria-label={`${t("typed_fields:delete_stake")} — ${stake.ownerName}`}
                className={cn(
                  "inline-flex items-center justify-center h-8 w-8 rounded-[7px]",
                  "text-dim hover:text-red hover:bg-red-soft",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-red/30",
                )}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        )}
      </div>

      {showEditForm && canEdit && (
        <StakeEditForm
          entityId={entityId}
          stake={stake}
          onDone={() => setShowEditForm(false)}
          pencilRef={pencilRef}
        />
      )}
    </li>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function OwnershipSkeleton(): JSX.Element {
  return (
    <ul className="divide-y divide-border-2">
      {Array.from({ length: 2 }, (_, i) => (
        <li key={i} className="flex items-center gap-3 py-3">
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-48" />
            <div className="skeleton h-3 w-20" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface OwnershipEditorProps {
  entityId: string;
  canEdit: boolean;
}

export function OwnershipEditor({ entityId, canEdit }: OwnershipEditorProps): JSX.Element {
  const { t } = useTranslation("typed_fields");
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["entity-ownership", entityId],
    queryFn: () => fetchOwnership(entityId),
    enabled: Boolean(entityId),
  });

  const stakes = data?.stakes ?? [];

  return (
    <div className="space-y-2">
      {/* Section header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[10px] font-bold tracking-[.05em] uppercase text-dim">
          {t("ownership_section")}
        </p>
        {canEdit && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className={cn(
              "text-accent font-semibold text-[13px] inline-flex items-center gap-1.5 py-[7px] cursor-pointer",
              "hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded",
            )}
          >
            <Plus className="h-[14px] w-[14px]" aria-hidden />
            {t("add_stake")}
          </button>
        )}
      </div>

      {showAdd && canEdit && (
        <AddStakeForm entityId={entityId} onDone={() => setShowAdd(false)} />
      )}

      {isError ? (
        <p className="text-[13px] text-muted py-1">{t("load_error_ownership")}</p>
      ) : isLoading ? (
        <OwnershipSkeleton />
      ) : stakes.length === 0 && !showAdd ? (
        <p className="text-[13.5px] text-dim py-1 italic">{t("no_stakes_recorded") || "No ownership stakes recorded"}</p>
      ) : (
        <ul className="divide-y divide-border-2">
          {stakes.map((stake) => (
            <StakeRow
              key={stake.id}
              entityId={entityId}
              stake={stake}
              canEdit={canEdit}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
